import FrameConfig from "@lib/classes/FrameConfig";

/** The query param the CMS/maFrame embed writes with the framing page's own URL. */
const PARENT_URL_PARAM = "ac[current_parent_url]";

/**
 * The framing page's own URL for the "Hosted at" traffic header (T16) — the
 * `ac[current_parent_url]` the CMS/maFrame embed passes when it iframes the booking engine.
 * Handed to the shared Apollo client as a PER-REQUEST getter that getClientHostPage PREFERS
 * over its own best-effort detection: the engine runs on a different host than the framing
 * page (`reservations.customer.com` embedded on `www.customer.com/city`), so the browser
 * hides the parent's path and detection can only ever see the bare domain — but the embed
 * already told us the exact page, and this is it. Undefined when the engine was NOT embedded
 * (a direct top-level load carries no param) → getClientHostPage falls back to its own
 * detection.
 *
 * TWO sources, current-URL FIRST:
 *  1. The live URL's `ac[current_parent_url]` param. The freshest, most authoritative record
 *     of the framing page for THIS load. Preferring it fixes two gaps where the persisted copy
 *     is not there yet or ever: FrameConfigProvider persists it only AFTER its bootstrap
 *     content query returns (so the first requests of a session would otherwise miss it), and
 *     it skips persistence entirely on checkout/booking routes (`EXCLUDE_URL_CONFIG_PATHS`).
 *     Current-URL-first also avoids returning a parentUrl a PRIOR embed left in per-tab
 *     storage when this load carries its own.
 *  2. The persisted `FrameConfig.parentUrl`. Covers the rest of the session: once the app
 *     client-navigates, the query param is stripped from the URL, but the captured copy
 *     survives.
 *
 * A GETTER read fresh per request (mirrors getServiceAreaId): the value is volatile per-tab
 * state that must not be captured once when the client is built.
 *
 * CRASH-SAFE: runs on EVERY browser nAPI request inside the Apollo authLink; reading
 * `window.location` / constructing FrameConfig (which touches sessionStorage) can THROW in
 * sandboxed / privacy-mode iframes, so each source is guarded independently and any throw
 * yields undefined (→ hint omitted → best-effort fallback), never a rejected request. The URL
 * source is guarded separately from storage so a privacy-mode embed (sessionStorage blocked)
 * still recovers the hint from its URL.
 *
 * A stable module-level reference on purpose: ApolloProvider memoizes the Apollo client on
 * this getter's identity (like getServiceAreaId), so a fresh closure each render would
 * needlessly recreate it.
 */
export const getHostPageParentUrl = (): string | undefined => {
  try {
    if (typeof window !== "undefined") {
      const fromUrl = new URLSearchParams(window.location.search).get(PARENT_URL_PARAM);
      if (fromUrl) return fromUrl;
    }
  } catch {
    // window.location access threw (sandboxed iframe) — fall through to the persisted copy.
  }
  try {
    return new FrameConfig().parentUrl || undefined;
  } catch {
    return undefined;
  }
};
